import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Mail, Lock, User, Phone, ArrowRight, Shield, Award, Clock, CheckCircle } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";

const CAPTCHA_SITE_KEY = process.env.REACT_APP_CAPTCHA_SITE_KEY;

const AuthPage = () => {
  const [activeTab, setActiveTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const captchaRef = useRef(null);

  // Form states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginCaptcha, setLoginCaptcha] = useState(null);
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerMobile, setRegisterMobile] = useState("");

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate(user.role === "admin" ? "/admin" : user.role === "organiser" ? "/organiser" : "/dashboard");
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (CAPTCHA_SITE_KEY && !loginCaptcha) {
      toast.error("Please complete the CAPTCHA verification");
      return;
    }
    setLoading(true);
    try {
      const userData = await login(loginEmail, loginPassword, loginCaptcha);
      toast.success(`Welcome back, ${userData.full_name}!`);
      navigate(userData.role === "admin" ? "/admin" : userData.role === "organiser" ? "/organiser" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
      if (captchaRef.current) captchaRef.current.reset();
      setLoginCaptcha(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await register(registerName, registerEmail, registerPassword, registerMobile);
      toast.success(`Welcome to edSolutions360, ${userData.full_name}!`);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container" data-testid="auth-page">
      {/* Left Section - Form */}
      <div className="auth-form-section gradient-mesh">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="mb-10 animate-fade-in">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                edSolutions360
              </span>
            </div>
            <p className="text-slate-500 text-sm">Examination Portal</p>
          </div>

          {/* Auth Card */}
          <Card className="border-0 shadow-xl shadow-slate-200/50 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {activeTab === "login" ? "Welcome Back" : "Create Account"}
              </CardTitle>
              <CardDescription>
                {activeTab === "login" 
                  ? "Sign in to access your assessments" 
                  : "Register to start your assessment journey"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="form-group">
                      <Label htmlFor="login-email" className="form-label">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="you@example.com"
                          className="pl-10 h-11"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <Label htmlFor="login-password" className="form-label">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="Enter your password"
                          className="pl-10 h-11"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          required
                          data-testid="login-password-input"
                        />
                      </div>
                    </div>
                    {CAPTCHA_SITE_KEY && (
                      <div className="flex justify-center" data-testid="login-captcha">
                        <ReCAPTCHA
                          ref={captchaRef}
                          sitekey={CAPTCHA_SITE_KEY}
                          onChange={(token) => setLoginCaptcha(token)}
                          onExpired={() => setLoginCaptcha(null)}
                          theme="light"
                          size="normal"
                        />
                      </div>
                    )}
                    <Button 
                      type="submit" 
                      className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 btn-active"
                      disabled={loading || (CAPTCHA_SITE_KEY && !loginCaptcha)}
                      data-testid="login-submit-btn"
                    >
                      {loading ? (
                        <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white" />
                      ) : (
                        <>
                          Sign In
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="form-group">
                      <Label htmlFor="register-name" className="form-label">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="John Doe"
                          className="pl-10 h-11"
                          value={registerName}
                          onChange={(e) => setRegisterName(e.target.value)}
                          required
                          data-testid="register-name-input"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <Label htmlFor="register-email" className="form-label">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="you@example.com"
                          className="pl-10 h-11"
                          value={registerEmail}
                          onChange={(e) => setRegisterEmail(e.target.value)}
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <Label htmlFor="register-mobile" className="form-label">Mobile Number (Optional)</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="register-mobile"
                          type="tel"
                          placeholder="+91 98765 43210"
                          className="pl-10 h-11"
                          value={registerMobile}
                          onChange={(e) => setRegisterMobile(e.target.value)}
                          data-testid="register-mobile-input"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <Label htmlFor="register-password" className="form-label">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="Create a strong password"
                          className="pl-10 h-11"
                          value={registerPassword}
                          onChange={(e) => setRegisterPassword(e.target.value)}
                          required
                          minLength={6}
                          data-testid="register-password-input"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 btn-active"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white" />
                      ) : (
                        <>
                          Create Account
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Features */}
          <div className="mt-8 grid grid-cols-3 gap-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mx-auto mb-2">
                <Shield className="w-5 h-5 text-indigo-600" />
              </div>
              <span className="text-xs text-slate-500">Secure</span>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mx-auto mb-2">
                <Award className="w-5 h-5 text-indigo-600" />
              </div>
              <span className="text-xs text-slate-500">Adaptive</span>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mx-auto mb-2">
                <Clock className="w-5 h-5 text-indigo-600" />
              </div>
              <span className="text-xs text-slate-500">Timed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Image/Branding */}
      <div className="auth-image-section hidden lg:flex flex-col items-center justify-center p-12 relative">
        <div className="relative z-10 text-center text-white max-w-lg">
          <h1 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Hire the Best Talent with Intelligent Assessments
          </h1>
          <p className="text-white/80 text-lg mb-10">
            Our adaptive examination platform evaluates candidates with precision, 
            helping you identify top talent for Business Analyst, MERN Developer, and Frontend Developer roles.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-left bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <CheckCircle className="w-6 h-6 text-emerald-300 flex-shrink-0" />
              <span>Adaptive difficulty based on performance</span>
            </div>
            <div className="flex items-center gap-4 text-left bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <CheckCircle className="w-6 h-6 text-emerald-300 flex-shrink-0" />
              <span>300+ curated interview-quality questions</span>
            </div>
            <div className="flex items-center gap-4 text-left bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <CheckCircle className="w-6 h-6 text-emerald-300 flex-shrink-0" />
              <span>Automatic ranking and recommendations</span>
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-blue-300/20 rounded-full blur-3xl" />
      </div>
    </div>
  );
};

export default AuthPage;
